import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface AwsWafConfig {
  challengeSdkUrl: string;
  captchaSdkUrl: string;
  isEnabled?: boolean;
}

export class AwsWafProvider implements Provider {
  type = ProviderType.AWS_WAF;
  isEnabled: boolean;
  loggingEnabled = false;
  private initialized = false;
  private config: AwsWafConfig;

  constructor(config: AwsWafConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Load AWS WAF Challenge SDK
      await this.loadScript(this.config.challengeSdkUrl, 'aws-waf-challenge-sdk');

      // Load AWS WAF Captcha SDK
      await this.loadScript(this.config.captchaSdkUrl, 'aws-waf-captcha-sdk');

      this.initialized = true;

      if (this.loggingEnabled) {
        logger.info('AWS WAF SDKs initialized successfully');
      }

      return true;
    } catch (error) {
      logger.error('Failed to initialize AWS WAF SDKs:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    // AWS WAF 通常不需要主动追踪事件，SDK 会自动处理
    if (this.loggingEnabled) {
      logger.info(`AWS WAF, Event: ${event.eventName}`, event.properties);
    }
  }

  /**
   * 启用 AWS WAF 日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用 AWS WAF 日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private async loadScript(src: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // 检查脚本是否已经加载
        if (document.getElementById(id)) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.id = id;
        script.type = 'text/javascript';
        script.src = src;
        script.defer = true;

        script.onload = () => {
          if (this.loggingEnabled) {
            logger.info(`AWS WAF script loaded: ${id}`);
          }
          resolve();
        };

        script.onerror = () => {
          reject(new Error(`Failed to load AWS WAF script: ${id}`));
        };

        // Insert script into DOM
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode?.insertBefore(script, firstScript);
      } catch (error) {
        reject(error);
      }
    });
  }
}
